import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material';
import { locationService } from '../services/locationService';
import { locationAvailabilityService } from '../services/locationAvailabilityService';
import { availabilityService } from '../services/availabilityService';
import { useSentryTracking } from './SentryComponentWrapper';
import { 
  trackUIInteraction, 
  addBreadcrumb, 
  logger 
} from '../utils/sentryUtils';

const TeamSupervisorAvailabilityForm = ({ householdId, students, onAvailabilitiesChange, userId }) => {
  // Initialize Sentry tracking for this component
  useSentryTracking('TeamSupervisorAvailabilityForm', { includeUpdates: true, includeRender: true });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  
  const [locations, setLocations] = useState([]);
  const [locationAvailabilities, setLocationAvailabilities] = useState({});
  const [userAvailabilities, setUserAvailabilities] = useState({});
  
  // Load locations and their availabilities
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load all locations
        const allLocations = await locationService.getAllActiveLocations();
        
        // Filter locations to show only:
        // 1. GATE (hub location) - always available
        // 2. Schools of students in the household
        const studentSchools = students
          .map(student => student.school)
          .filter(school => school && school.trim() !== ''); // Filter out empty/null schools
        
        const filteredLocations = allLocations.filter(location => {
          // Always include GATE (hub location)
          if (location.isHub) return true;
          
          // Include schools of students in the household
          return studentSchools.some(school => location.name === school);
        });
        
        // Sort locations: hub location first, then student schools
        const sortedLocations = filteredLocations.sort((a, b) => {
          // Hub locations always come first
          if (a.isHub && !b.isHub) return -1;
          if (!a.isHub && b.isHub) return 1;
          
          // If both are hub or both are not hub, maintain original order
          return 0;
        });
        
        setLocations(sortedLocations);
        
        // Load location availabilities for filtered locations only
        const availabilities = {};
        for (const location of filteredLocations) {
          try {
            const locationAvails = await locationAvailabilityService.getAvailabilitiesByLocationId(location.id);
            availabilities[location.id] = locationAvails;
          } catch (err) {
            console.warn(`Could not load availabilities for location ${location.name}:`, err);
            availabilities[location.id] = [];
          }
        }
        setLocationAvailabilities(availabilities);
        
        // Load existing user availabilities if userId is provided
        if (userId) {
          try {
            const existingAvailabilities = await availabilityService.getUserAvailabilityByUserId(userId);
            
            // Convert existing availabilities to the format expected by the form
            const existingUserAvails = {};
            existingAvailabilities.forEach(avail => {
              if (avail.isAvailable) {
                const key = `${avail.locationId}_${avail.dayOfWeek}_${avail.startTime}_${avail.endTime}`;
                existingUserAvails[key] = true;
              }
            });
            
            setUserAvailabilities(existingUserAvails);
          } catch (err) {
            console.warn('Could not load existing user availabilities:', err);
            // Continue without existing availabilities
          }
        }
        
      } catch (err) {
        console.error('Error loading availability data:', err);
        setError('Failed to load availability data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [students, userId]);

  const handleAvailabilityChange = (locationId, dayOfWeek, startTime, endTime, isAvailable) => {
    // Track the availability change interaction
    trackUIInteraction('availability_change', {
      locationId,
      dayOfWeek,
      startTime,
      endTime,
      isAvailable,
      userId
    });
    
    addBreadcrumb("Availability changed", "ui.interaction", {
      locationId,
      dayOfWeek,
      startTime,
      endTime,
      isAvailable,
      userId
    });
    
    const key = `${locationId}_${dayOfWeek}_${startTime}_${endTime}`;
    
    if (isAvailable) {
      setUserAvailabilities(prev => ({ ...prev, [key]: true }));
    } else {
      setUserAvailabilities(prev => {
        const newAvails = { ...prev };
        delete newAvails[key];
        return newAvails;
      });
    }
    
    // Automatically update parent component with new availabilities
    const updatedAvailabilities = [];
    const newUserAvails = isAvailable 
      ? { ...userAvailabilities, [key]: true }
      : (() => {
          const newAvails = { ...userAvailabilities };
          delete newAvails[key];
          return newAvails;
        })();
    
    Object.entries(newUserAvails).forEach(([availKey, availIsAvailable]) => {
      if (availIsAvailable) {
        const [locId, day, start, end] = availKey.split('_');
        updatedAvailabilities.push({
          locationId: locId,
          dayOfWeek: day,
          startTime: start,
          endTime: end,
          isAvailable: Boolean(availIsAvailable)
        });
      }
    });
    
    onAvailabilitiesChange(updatedAvailabilities);
    
    // Show brief success feedback
    setLastUpdateTime(new Date());
    setSuccess('Availability updated automatically');
    setTimeout(() => setSuccess(''), 2000);
    
    logger.info("Team supervisor availability updated", {
      locationId,
      dayOfWeek,
      startTime,
      endTime,
      isAvailable,
      userId,
      householdId
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  const formatDay = (day) => {
    return day.charAt(0) + day.slice(1).toLowerCase();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (locations.length === 0) {
    return (
      <Alert severity="info">
        No locations available for team supervisor availabilities. Please ensure students have schools assigned or contact an administrator.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Set your availability for team supervision. Available locations are based on your students' schools plus GATE (hub location).
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        Your availabilities are automatically saved as you make changes. No need to click a save button!
      </Alert>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Group availabilities by location, then by day type (like original styling) */}
      {locations.map((location) => {
        const locationAvails = locationAvailabilities[location.id] || [];
        
        // Only show locations that have availabilities
        if (locationAvails.length === 0) return null;

        // Separate weekday and Saturday availabilities
        const weekdayAvails = locationAvails.filter(avail => 
          ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'].includes(avail.dayOfWeek)
        );
        const saturdayAvails = locationAvails.filter(avail => avail.dayOfWeek === 'SATURDAY');
        const sundayAvails = locationAvails.filter(avail => avail.dayOfWeek === 'SUNDAY');

        return (
          <Card key={location.id} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: location.isHub ? 'primary.main' : 'text.primary' }}>
                {location.name}
                {location.isHub && ' (Hub Location)'}
              </Typography>
              
              {/* Weekday Slots Section */}
              {weekdayAvails.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
                    Weekday Slots
                  </Typography>
                  
                  {/* Group by individual days in boxes */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'].map((day) => {
                      const dayAvails = weekdayAvails.filter(avail => avail.dayOfWeek === day);
                      if (dayAvails.length === 0) return null;

                      return (
                        <Card key={day} variant="outlined" sx={{ minWidth: 200, flex: '1 1 200px' }}>
                          <CardContent sx={{ py: 2, px: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary', textAlign: 'center' }}>
                              {formatDay(day)}
                            </Typography>
                            
                            {dayAvails.map((availability) => {
                              const key = `${location.id}_${availability.dayOfWeek}_${availability.startTime}_${availability.endTime}`;
                              const isChecked = userAvailabilities[key] || false;
                              
                              return (
                                <Box key={availability.id} sx={{ mb: 1 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isChecked}
                                        onChange={(e) => handleAvailabilityChange(
                                          location.id,
                                          availability.dayOfWeek,
                                          availability.startTime,
                                          availability.endTime,
                                          e.target.checked
                                        )}
                                        size="small"
                                      />
                                    }
                                    label={
                                      <Typography variant="body2">
                                        {formatTime(availability.startTime)} - {formatTime(availability.endTime)}
                                      </Typography>
                                    }
                                  />
                                </Box>
                              );
                            })}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              )}
              
              {/* Saturday Slots Section */}
              {saturdayAvails.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
                    Saturday Slots
                  </Typography>
                  
                  {/* Horizontal layout for Saturday slots like original */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 2 }}>
                    {saturdayAvails.map((availability) => {
                      const key = `${location.id}_${availability.dayOfWeek}_${availability.startTime}_${availability.endTime}`;
                      const isChecked = userAvailabilities[key] || false;
                      
                      return (
                        <FormControlLabel
                          key={availability.id}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => handleAvailabilityChange(
                                location.id,
                                availability.dayOfWeek,
                                availability.startTime,
                                availability.endTime,
                                e.target.checked
                              )}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {formatTime(availability.startTime)} - {formatTime(availability.endTime)}
                            </Typography>
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              
              {/* Sunday Slots Section */}
              {sundayAvails.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
                    Sunday Slots
                  </Typography>
                  
                  {/* Horizontal layout for Sunday slots like original */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 2 }}>
                    {sundayAvails.map((availability) => {
                      const key = `${location.id}_${availability.dayOfWeek}_${availability.startTime}_${availability.endTime}`;
                      const isChecked = userAvailabilities[key] || false;
                      
                      return (
                        <FormControlLabel
                          key={availability.id}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => handleAvailabilityChange(
                                location.id,
                                availability.dayOfWeek,
                                availability.startTime,
                                availability.endTime,
                                e.target.checked
                              )}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {formatTime(availability.startTime)} - {formatTime(availability.endTime)}
                            </Typography>
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}
      
      {/* Remove the Save Availabilities button section */}
      {/* <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? <CircularProgress size={24} /> : 'Save Availabilities'}
        </Button>
      </Box> */}
    </Box>
  );
};

export default TeamSupervisorAvailabilityForm;
